import {
  useDeleteExportFileService,
  useExportFileDownloadUrl,
} from '@/context/services/export_files/ExportFiles.service';
import { useExportFilesSearchRefetch } from '@/context/services/export_files/refetchContext';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useDownloadClick } from './useDownload';

export type ActionHandlers = {
  onDownloadClick: (fileId: string, fileName: string) => Promise<void>;
  onDeleteClick: (fileId: string) => Promise<void>;
};

export const useExportFileActions = (
  confirm: () => Promise<boolean>
): ActionHandlers => {
  const { t } = useTranslation();
  const { generateDownloadUrl } = useExportFileDownloadUrl();
  const { deleteExportFile } = useDeleteExportFileService();
  const notify = useToastAPI();
  const refetch = useExportFilesSearchRefetch();
  const download = useDownloadClick();

  const onDownloadClick = useCallback(
    async (fileId: string, fileName: string) => {
      try {
        const { status, presignedUrl, errors } = await generateDownloadUrl(
          fileId
        );

        if (status === 'success' && presignedUrl) {
          download(presignedUrl, fileName);
        } else if (status === 'error' && errors) {
          notify.error({
            title: errors.messages?.join(', ') || t('system_error'),
            duration: 3,
          });
        }
      } catch (error) {
        let message;
        if (error instanceof Error) {
          throw error;
        } else {
          message = t('system_error');
        }
        throw new Error(message);
      }
    },
    [generateDownloadUrl, t, notify, download]
  );

  const onDeleteClick = useCallback(
    async (fileId: string) => {
      const confirmed = await confirm();
      if (!confirmed) return;
      try {
        const response = await deleteExportFile(fileId);
        const { status, errors } = response ?? {};
        if (status === 'success') {
          refetch?.();
        } else if (status === 'error' && errors) {
          notify.error({
            title:
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              (errors as AxiosResponse)?.data?.messages?.join(', ') ||
              t('system_error'),
            duration: 3,
          });
        }
      } catch (error) {
        if (error instanceof Error && error.message !== t('system_error')) {
          throw error;
        }
        notify.error({
          title: t('system_error'),
          duration: 3,
        });
      }
    },
    [confirm, deleteExportFile, refetch, notify, t]
  );

  return {
    onDownloadClick,
    onDeleteClick,
  };
};
